import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, Container, Grid, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Footer, MarketingFinalCta, ProblemSection, SectionHeading } from '../components/homepage';
import CheckoutStackCard from '../components/homepage/graphics/CheckoutStackCard';
import HeroOptimizationCore from '../components/homepage/graphics/HeroOptimizationCore';
import { PaymentTriangle, ScalesOfOptimization } from '../components/homepage/artifacts';
import { DEMO_REQUEST_MAILTO } from '../config/contact';
import {
  marketingCardSx,
  marketingDisplayFont,
  marketingHeroGridSx,
  marketingMutedSectionSx,
  marketingPalette,
  marketingPrimaryButtonSx,
  marketingSecondaryButtonSx,
  marketingSectionSx,
} from '../components/homepage/marketingSystem';

type ProofVisual = {
  kind: 'beforeAfter' | 'singleDelta';
  heroValue: string;
  heroLabel: string;
  // For beforeAfter: show a bar from `before` to `after`
  before?: { label: string; value: number; display: string };
  after?: { label: string; value: number; display: string };
  scaleMax?: number;
  // For singleDelta: a proof chip underneath
  supporting?: { label: string; value: string };
  // Secondary chip shown beneath the hero block (in both kinds)
  preserved: string;
};

type ProofCase = {
  slug: string;
  segmentLabel: string;
  pilotTag: string;
  headline: string;
  visual: ProofVisual;
};

const proofCases: ProofCase[] = [
  {
    slug: 'luxury-goods-resale',
    segmentLabel: 'Luxury goods resale',
    pilotTag: 'Pilot one',
    headline: 'Checkout conversion recovered without giving up risk control on high-value baskets.',
    visual: {
      kind: 'beforeAfter',
      heroValue: '+15 pts',
      heroLabel: 'Checkout conversion',
      before: { label: 'Before', value: 57, display: '57%' },
      after: { label: 'After', value: 72, display: '72%' },
      scaleMax: 100,
      preserved: 'High-value risk controls stayed intact.',
    },
  },
  {
    slug: 'marketplace-multinational',
    segmentLabel: 'Marketplace multinational',
    pilotTag: 'Pilot two',
    headline: 'Blended cost and chargeback pressure dropped together — without narrowing checkout coverage.',
    visual: {
      kind: 'singleDelta',
      heroValue: '−8%',
      heroLabel: 'Blended payment cost',
      supporting: { label: 'Chargebacks in key markets', value: '< 0.5%' },
      preserved: 'Checkout coverage stayed broad across the marketplace footprint.',
    },
  },
];

const LandingPage: React.FC = () => {
  return (
    <Box className="marketing-layout" sx={{ width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <link rel="canonical" href="https://ormal.io/" />
      </Helmet>
      {/* Hero: 2-column layout on md+ — text left, HeroOptimizationCore focal visual right */}
      <Box
        component="section"
        sx={{
          ...marketingSectionSx,
          pt: { xs: 5.5, md: 8 },
          pb: { xs: 6, md: 8 },
          overflow: 'hidden',
          background:
            'radial-gradient(900px 600px at 85% 20%, rgba(15,111,255,0.18), transparent 58%), radial-gradient(600px 400px at 12% 70%, rgba(14,165,233,0.10), transparent 52%), linear-gradient(180deg, #ffffff 0%, #f4f9ff 100%)',
          borderBottom: '1px solid rgba(219,228,239,0.72)',
          position: 'relative',
        }}
      >
        <Box sx={marketingHeroGridSx} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 5 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: marketingDisplayFont,
                    fontSize: { xs: '2.15rem', md: '2.85rem', lg: '3.4rem' },
                    fontWeight: 300,
                    letterSpacing: '-0.005em',
                    color: marketingPalette.headline,
                    lineHeight: 1.12,
                  }}
                >
                  Payments move in real time.{' '}
                  <Box
                    component="span"
                    sx={{
                      background:
                        'linear-gradient(90deg, #0f6fff 0%, #3ea1ff 55%, #7c3aed 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Your strategy should too.
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    mt: 2,
                    color: marketingPalette.body,
                    fontSize: { xs: '1rem', md: '1.06rem' },
                    lineHeight: 1.65,
                    fontWeight: 400,
                    maxWidth: 520,
                  }}
                >
                  Ormal studies your payment history and writes the strategy a payments team would — per customer group, reasoning attached. One call returns the method stack, processor route, and MID in under 150ms. Your existing stack still executes.
                </Typography>
                <Box sx={{ mt: 2.8, display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
                  <Button component="a" href={DEMO_REQUEST_MAILTO} endIcon={<ArrowForward />} sx={marketingPrimaryButtonSx}>
                    Request Demo
                  </Button>
                  <Button component={RouterLink} to="/how-it-works" sx={marketingSecondaryButtonSx}>
                    See how it works
                  </Button>
                </Box>

                {/* Proof strip */}
                <Box
                  sx={{
                    mt: 3.2,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1.4,
                    width: '100%',
                    maxWidth: 520,
                  }}
                >
                  {[
                    { value: '+15 pts', label: 'Checkout conversion', detail: 'Luxury resale pilot' },
                    { value: '−8%',     label: 'Blended payment cost', detail: 'Multinational marketplace' },
                    { value: '1 week',  label: 'To a measured number', detail: 'Your history, replayed' },
                  ].map((s) => (
                    <Box
                      key={s.label}
                      sx={{
                        pl: 1.3,
                        borderLeft: '2px solid rgba(15,111,255,0.35)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: marketingDisplayFont,
                          fontSize: { xs: '1.05rem', md: '1.18rem' },
                          fontWeight: 400,
                          color: marketingPalette.accent,
                          letterSpacing: '-0.02em',
                          lineHeight: 1.1,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {s.value}
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', fontWeight: 400, color: marketingPalette.headline, lineHeight: 1.25 }}>
                        {s.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.64rem', fontWeight: 400, color: marketingPalette.muted, lineHeight: 1.3 }}>
                        {s.detail}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <HeroOptimizationCore />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Problem framing — three reasons existing payment teams still lose
          performance. Sets up the anti-positioning that follows. */}
      <ProblemSection />

      {/* Bridge: Ormal is beside the path, not in it. Keeps it brief — the problem
          section above sets context, the platform page carries the full explanation. */}
      <Box
        component="section"
        sx={{
          py: { xs: 3.5, md: 4.5 },
          background: '#ffffff',
          borderBottom: '1px solid rgba(219,228,239,0.72)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 2, md: 0 },
              divideX: '1px solid rgba(219,228,239,0.72)',
            }}
          >
            {[
              {
                label: 'Beside the path, not in it',
                detail: 'No funds-flow change. No migration. No lock-in. Your existing PSPs execute exactly as before.',
              },
              {
                label: 'Real-time, not retrospective',
                detail: 'One decision per transaction in <150ms — not last month\'s dashboard.',
              },
              {
                label: 'Per-call pricing',
                detail: 'Not a percentage of your GMV. You pay for decisions, not volume.',
              },
            ].map((item, i) => (
              <Box
                key={item.label}
                sx={{
                  px: { xs: 0, md: i === 0 ? 0 : 3.5 },
                  borderLeft: { xs: 'none', md: i === 0 ? 'none' : '1px solid rgba(219,228,239,0.72)' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: marketingPalette.headline,
                    mb: 0.5,
                    lineHeight: 1.3,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: marketingPalette.body, lineHeight: 1.6 }}>
                  {item.detail}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Flow: payment intents → Ormal Optimization → optimized stack. Lives right
          below the hero so the 3-column composition is still front and center, but it's
          a section, not the hero visual itself. */}
      <Box
        component="section"
        sx={{
          ...marketingSectionSx,
          pt: { xs: 5, md: 6 },
          pb: { xs: 5, md: 6 },
          background: 'linear-gradient(180deg, #f4f9ff 0%, #ffffff 100%)',
        }}
      >
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="How a payment clears"
            title="Payment intents in. Optimized stack out. Every time."
            description="Each intent is scored against the priority weights in real time. The coordinated stack the buyer sees is the composition the optimization chose for that specific intent, in that context."
            marginBottom={3.2}
          />
          <CheckoutStackCard />
        </Container>
      </Box>

      {/* Optimization posture: the triangle IS the product explanation. Where you sit
          on cost/conversion/risk defines the posture the optimization takes across
          the full stack. No method names, no example compositions. */}
      <Box component="section" sx={marketingMutedSectionSx} id="triangle">
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Optimization posture"
            title="Your priority weights are the axis."
            description="Where you sit on cost, conversion, and risk is where Ormal optimizes from. Shift the weights and the posture across every decision shifts with them."
            marginBottom={3.5}
          />
          <PaymentTriangle />
          <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'center' }}>
            <Button
              component={RouterLink}
              to="/solution"
              endIcon={<ArrowForward />}
              sx={{
                textTransform: 'none',
                color: marketingPalette.accent,
                fontWeight: 400,
                fontSize: '0.9rem',
                '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
              }}
            >
              See how Ormal balances all three
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Two scales: per-intent (micro) and across-the-book (macro). Same optimization,
          same priority weights, operating at both layers simultaneously. */}
      <Box component="section" sx={marketingSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Two scales"
            title="Every intent is optimized. So is the book."
            description="Per-transaction optimization clears each payment against your priority weights. Portfolio optimization keeps that stream of individual decisions aligned with the commercial pressures that only show up at scale — tier pricing thresholds, volume commitments, counterparty concentration, and the blended shape of the book. Ormal operates both layers against the same weights, so short-term routing decisions compound into long-term commercial posture instead of fighting it."
            marginBottom={3.5}
          />
          <ScalesOfOptimization />
        </Container>
      </Box>

      {/* Proof of value: one hero metric per pilot, visualized. KPI movement lives
          here — this is the outcome of the product, so we don't split it into a
          separate section. */}
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 5, md: 6.5 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Proof of value"
            title="Six completed pilots. Two published in full, with the numbers the business tracks."
            description="Each pilot optimized against a different priority weighting. The posture shifted; the stack composed itself accordingly; the outcome is the delta."
            marginBottom={3.2}
          />
          <Grid container spacing={2.6} alignItems="stretch">
            {proofCases.map((item, index) => {
              const accent = index === 0 ? '#0f6fff' : '#0b57d0';
              const accentSoft = `${accent}14`;
              const accentBorder = `${accent}38`;
              return (
                <Grid item xs={12} md={6} key={item.slug}>
                  <Card
                    sx={{
                      ...marketingCardSx,
                      p: { xs: 2.6, md: 3.2 },
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Subtle accent glow in the corner */}
                    <Box
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        top: -60,
                        right: -60,
                        width: 180,
                        height: 180,
                        borderRadius: '50%',
                        background: `radial-gradient(closest-side, ${accent}22, transparent 70%)`,
                        pointerEvents: 'none',
                      }}
                    />

                    {/* Header row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
                      <Box
                        sx={{
                          px: 0.9,
                          py: 0.5,
                          borderRadius: 0.7,
                          backgroundColor: accentSoft,
                          border: `1px solid ${accentBorder}`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: accent,
                            fontSize: '0.62rem',
                            fontWeight: 400,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            lineHeight: 1,
                          }}
                        >
                          {item.pilotTag}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: marketingPalette.muted, fontSize: '0.78rem', fontWeight: 400 }}>
                        {item.segmentLabel}
                      </Typography>
                    </Box>

                    {/* Hero metric visualization */}
                    <Box
                      sx={{
                        position: 'relative',
                        p: { xs: 1.8, md: 2.2 },
                        borderRadius: 1.1,
                        border: `1px solid ${accentBorder}`,
                        background: `linear-gradient(180deg, ${accent}0A 0%, ${accent}02 100%)`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.2, flexWrap: 'wrap' }}>
                        <Typography
                          sx={{
                            fontFamily: marketingDisplayFont,
                            color: accent,
                            fontWeight: 400,
                            fontSize: { xs: '2.4rem', md: '2.9rem' },
                            lineHeight: 1,
                            letterSpacing: '-0.04em',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {item.visual.heroValue}
                        </Typography>
                        <Typography
                          sx={{
                            color: marketingPalette.headline,
                            fontSize: { xs: '0.92rem', md: '0.98rem' },
                            fontWeight: 400,
                            letterSpacing: '-0.005em',
                          }}
                        >
                          {item.visual.heroLabel}
                        </Typography>
                      </Box>

                      {item.visual.kind === 'beforeAfter' && item.visual.before && item.visual.after && (
                        <Box sx={{ mt: 1.6, display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                          {(['before', 'after'] as const).map((key) => {
                            const point = item.visual[key]!;
                            const scaleMax = item.visual.scaleMax ?? 100;
                            const pct = Math.min(100, (point.value / scaleMax) * 100);
                            const isAfter = key === 'after';
                            return (
                              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                                <Typography
                                  sx={{
                                    width: 58,
                                    color: marketingPalette.muted,
                                    fontSize: '0.62rem',
                                    fontWeight: 400,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {point.label}
                                </Typography>
                                <Box
                                  sx={{
                                    flex: 1,
                                    height: 12,
                                    borderRadius: 6,
                                    backgroundColor: 'rgba(15,23,42,0.06)',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: `${pct}%`,
                                      background: isAfter
                                        ? `linear-gradient(90deg, ${accent} 0%, ${accent}ee 55%, ${accent}bb 100%)`
                                        : 'linear-gradient(90deg, rgba(100,116,139,0.6) 0%, rgba(100,116,139,0.4) 100%)',
                                      borderRadius: 6,
                                      boxShadow: isAfter ? `0 0 10px ${accent}55` : 'none',
                                      transition: 'width 400ms ease',
                                    }}
                                  />
                                </Box>
                                <Typography
                                  sx={{
                                    width: 44,
                                    textAlign: 'right',
                                    color: isAfter ? accent : marketingPalette.body,
                                    fontWeight: 400,
                                    fontSize: '0.82rem',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {point.display}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {item.visual.kind === 'singleDelta' && item.visual.supporting && (
                        <Box
                          sx={{
                            mt: 1.6,
                            p: 1.1,
                            borderRadius: 0.9,
                            border: `1px solid ${accentBorder}`,
                            backgroundColor: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              color: marketingPalette.muted,
                              fontSize: '0.72rem',
                              fontWeight: 400,
                              letterSpacing: '0.02em',
                            }}
                          >
                            {item.visual.supporting.label}
                          </Typography>
                          <Typography
                            sx={{
                              color: accent,
                              fontSize: '1.05rem',
                              fontWeight: 400,
                              letterSpacing: '-0.01em',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {item.visual.supporting.value}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Headline prose */}
                    <Typography
                      sx={{
                        color: marketingPalette.headline,
                        fontWeight: 400,
                        fontSize: { xs: '1rem', md: '1.05rem' },
                        lineHeight: 1.42,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {item.headline}
                    </Typography>

                    {/* Preserved chip */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                        p: 1,
                        borderRadius: 0.8,
                        border: `1px solid ${marketingPalette.border}`,
                        backgroundColor: '#fbfdff',
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: '#047857',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          color: marketingPalette.body,
                          fontSize: '0.78rem',
                          fontWeight: 400,
                          lineHeight: 1.4,
                        }}
                      >
                        {item.visual.preserved}
                      </Typography>
                    </Box>

                    {/* CTA */}
                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        component={RouterLink}
                        to={`/case-studies/${item.slug}`}
                        endIcon={<ArrowForward />}
                        sx={{
                          ...marketingSecondaryButtonSx,
                          color: marketingPalette.headline,
                          borderColor: marketingPalette.border,
                        }}
                      >
                        Read case study
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Commercial context — answers the first-call objections before the final CTA. */}
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 4, md: 5 }, pb: { xs: 4, md: 5 }, backgroundColor: '#f8fbff' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.9fr) repeat(3, minmax(0, 1fr))' },
              gap: { xs: 2, md: 2.4 },
              alignItems: 'stretch',
            }}
          >
            <Box>
              <Typography sx={{ color: marketingPalette.accent, fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 0.9 }}>
                What a pilot looks like
              </Typography>
              <Typography sx={{ color: marketingPalette.headline, fontFamily: marketingDisplayFont, fontWeight: 400, fontSize: { xs: '1.25rem', md: '1.4rem' }, letterSpacing: '-0.02em', lineHeight: 1.22 }}>
                Low-risk by design. You keep your stack.
              </Typography>
            </Box>
            {[
              { label: 'Duration', value: '30–90 days', detail: 'Scoped to a surface or flow — we still optimize conversion, cost, and risk together.' },
              { label: 'Your stack', value: 'Stays in place', detail: 'No PSP or orchestrator replacement. Ormal reads, scores, returns.' },
              { label: 'Data posture', value: 'Stays with you', detail: 'Integrations run inside your environment. No raw PAN ever touches us.' },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  p: 1.8,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: marketingPalette.border,
                  backgroundColor: '#ffffff',
                  display: 'grid',
                  alignContent: 'start',
                  gap: 0.5,
                }}
              >
                <Typography sx={{ color: marketingPalette.muted, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  {item.label}
                </Typography>
                <Typography sx={{ color: marketingPalette.headline, fontFamily: marketingDisplayFont, fontWeight: 400, fontSize: '1.1rem', letterSpacing: '-0.015em', lineHeight: 1.2 }}>
                  {item.value}
                </Typography>
                <Typography sx={{ color: marketingPalette.body, fontSize: '0.82rem', lineHeight: 1.55 }}>
                  {item.detail}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Final band */}
      <MarketingFinalCta
        kicker="Get started"
        heading="Find out what's in your payment data."
        body="Share as little as 30 days of transaction history — 90 days gives us more signal. One week later you have a quantified opportunity report — or we tell you honestly there isn't one."
        secondary={{ label: 'See how it works', to: '/how-it-works' }}
      />

      <Footer />
    </Box>
  );
};

export default LandingPage;
